// Public receipt check (FR-21): anyone holding a downloaded receipt can confirm
// it was signed by the platform and not changed. No sign-in needed.
import { Alert, Button, FileButton, Group, Text, Textarea } from "@mantine/core";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { FormError, Page } from "../../shared/ui/Page";
import { parseReceiptFile } from "./receiptFile";

export function VerifyReceiptPage() {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [verdict, setVerdict] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file: File | null) => {
    if (!file) return;
    setVerdict(null);
    setError(null);
    setText(await file.text());
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setVerdict(null);
    setError(null);
    const body = parseReceiptFile(text);
    if (!body) return setError(t("verify.bad_file"));
    setBusy(true);
    const { data, error } = await api.POST("/public/receipts/verify", { body });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setVerdict(data.valid);
  };

  return (
    <Page title={t("verify.title")}>
      <Text size="sm">{t("verify.intro")}</Text>
      <form onSubmit={submit}>
        <Textarea
          label={t("verify.paste")}
          value={text}
          onChange={(e) => {
            setText(e.currentTarget.value);
            setVerdict(null);
          }}
          autosize
          minRows={5}
          maxRows={14}
          spellCheck={false}
          styles={{ input: { fontFamily: "monospace" } }}
          mb="sm"
        />
        <Group mb="sm">
          <FileButton onChange={(f) => void upload(f)} accept="application/json,.json">
            {(props) => (
              <Button variant="default" {...props}>
                {t("verify.upload")}
              </Button>
            )}
          </FileButton>
          <Button type="submit" loading={busy}>
            {t("verify.submit")}
          </Button>
        </Group>
        <FormError message={error} />
        {verdict !== null && (
          <Alert color={verdict ? "green" : "red"} role="status" data-testid="receipt-verdict">
            {verdict ? t("verify.valid") : t("verify.invalid")}
          </Alert>
        )}
      </form>
    </Page>
  );
}
